/* eslint-disable no-console */
import getIn from "redux-form/lib/structure/plain/getIn";

import ajv from "../../lib/ajv";
// import i18n from "../../lib/i18n";
import moment from "../../lib/moment";
import { getStore } from "../../store";
import { isValidZipCodeNL } from "../../lib/formUtils";
import { getSelectedCartItemTitleFromContext } from "./util";

const getContext = () =>
  getStore()
    .getState()
    .cart.get("context");

ajv.addKeyword("regexp", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {}
  compile: schema =>
    function v(data, dataPath) {
      if (!schema.pattern) {
        return false;
      }

      try {
        const regexp = new RegExp(schema.pattern, schema.flags || "");

        if (!regexp.test(data)) {
          v.errors = [
            {
              keyword: "regexp",
              dataPath
            }
          ];
          return false;
        }
      } catch (err) {
        v.errors = [
          {
            keyword: "regexp",
            message: err,
            dataPath
          }
        ];
        return false;
      }

      return true;
    }
});

ajv.addKeyword("checkDiscountCode", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      const context = getContext();
      if (!context || !data.discountCode || !data.discountCode.trim()) {
        return true;
      }

      // ignore if not same code
      if (context.getIn(["discounts", "discountCode"], "") !== data.discountCode.trim()) {
        return true;
      }

      if (data.discountCode.trim() && !context.getIn(["discounts", "validCode"], false)) {
        v.errors = [
          {
            keyword: "checkDiscountCode",
            dataPath: `${dataPath}.discountCode`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkDetectedType", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      if (data && data.detectedType && /^PO box$/i.test(data.detectedType)) {
        v.errors = [
          {
            keyword: "checkDetectedType",
            dataPath: `${dataPath}.streetNumber`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkDeliveryDate", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      const context = getContext();
      if (!context) {
        return true;
      }

      if (!data.date) {
        v.errors = [
          {
            keyword: "required",
            params: {
              missingProperty: "date"
            },
            dataPath
          }
        ];
        return false;
      }

      // for cart view
      let matches = dataPath.match(/^\.recipients\[([\d]+)]/);
      if (matches === null) {
        // for product view
        matches = dataPath.match(/^\[([\d]+)].delivery$/);
        if (matches === null) {
          return true;
        }
      }

      const recipientIdx = parseInt(matches[1], 10);

      const availableDates = context.getIn(["recipientContexts", recipientIdx, "availableDates", "dates"]);
      if (!availableDates || !availableDates.size) {
        return true;
      }

      if (!availableDates.contains(data.date)) {
        v.errors = [
          {
            keyword: "checkDeliveryDate",
            dataPath: `${dataPath}.date`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkDeliveryTimeFrame", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      const context = getContext();
      if (!context) {
        return true;
      }

      if (!data.timeFrame) {
        v.errors = [
          {
            keyword: "required",
            params: {
              missingProperty: "timeFrame"
            },
            dataPath
          }
        ];
        return false;
      }

      // for cart view
      let matches = dataPath.match(/^\.recipients\[([\d]+)]/);
      if (matches === null) {
        // for product (step) view
        matches = dataPath.match(/^\[([\d]+)].delivery$/);
        if (matches === null) {
          return true;
        }
      }

      const recipientIdx = parseInt(matches[1], 10);

      const availableTimeFrames = context.getIn(["recipientContexts", recipientIdx, "availableTimeFrames"]);
      if (!availableTimeFrames || !availableTimeFrames.size) {
        return true;
      }

      if (availableTimeFrames.filter(timeFrame => timeFrame.get("id") === data.timeFrame).size === 0) {
        v.errors = [
          {
            keyword: "checkDeliveryTimeFrame",
            dataPath: `${dataPath}.timeFrame`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkAddressValidZipcode", {
  compile: () =>
    function v(data, dataPath) {
      if (!data || !data.countryCode || !data.zipcode) {
        return true;
      }

      if (data.countryCode === "NL" && !isValidZipCodeNL(data.zipcode.trim())) {
        v.errors = [
          {
            keyword: "checkAddressValidZipcode",
            message: "invalidnlzipcodeformat",
            params: {},
            dataPath: `${dataPath}.zipcode`
          }
        ];
        return false;
      }

      const context = getStore()
        .getState()
        .cart.get("context");

      let isValid = true;
      if (dataPath.indexOf("customer") !== -1 && context) {
        isValid = context.get("isValidCustomerZipcode", true);
      }

      if (/^(\.recipients)?\[\d+]/.test(dataPath) && context && context.has("recipientContexts")) {
        const recipientContextIdx = parseInt(dataPath.match(/\[(\d+)]/, "$1")[1], 10);
        isValid = context.getIn(["recipientContexts", recipientContextIdx, "isValidZipcode"], true);
      }

      if (!isValid) {
        v.errors = [
          {
            keyword: "checkAddressValidZipcode",
            message: "invalidzipcodecombination",
            params: [],
            dataPath: `${dataPath}.zipcode`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkAddressCountryCode", {
  compile: () =>
    function v(data, dataPath) {
      if (!data || !data.countryCode) {
        return true;
      }

      const context = getStore()
        .getState()
        .cart.get("context");
      if (!context) {
        // context is not available
        return true;
      }
      const deliveryCountryCodes = context.get("deliveryCountryCodes");
      if (!deliveryCountryCodes) {
        // context is not available
        return true;
      }

      const countryCode = deliveryCountryCodes.find(c => c.get("id") === data.countryCode);
      if (!countryCode) {
        v.errors = [
          {
            keyword: "checkAddressCountryCode",
            message: "checkAddressCountryCode",
            params: [],
            dataPath: `${dataPath}.countryCode`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkAddressNoWadden", {
  // $data: 'zipcode',
  compile: () =>
    function v(data, dataPath) {
      if (!data || !data.countryCode || data.countryCode !== "NL" || !data.zipcode) {
        return true;
      }
      const zipcodeDigits = parseInt(data.zipcode.replace(/\D+/g, ""), 10);
      if (
        (zipcodeDigits >= 1790 && zipcodeDigits <= 1799) ||
        (zipcodeDigits >= 8880 && zipcodeDigits <= 8899) ||
        (zipcodeDigits >= 9160 && zipcodeDigits <= 9169)
      ) {
        v.errors = [
          {
            keyword: "checkAddressNoWadden",
            message: "noMainlandNoService",
            params: [],
            dataPath: `${dataPath}.zipcode`
          }
        ];
        return false;
      }
      return true;
    }
});

ajv.addKeyword("checkServiceDateIsSameOrAfterDeliveryDate", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(recipientData, dataPath) {
      const context = getContext();
      if (!context || !recipientData) {
        return true;
      }

      if (
        !recipientData.address ||
        !recipientData.address.addition2 ||
        !recipientData.address.addressType ||
        (recipientData.address.addressType !== "funeral center" && recipientData.address.addressType !== "church")
      ) {
        return true;
      }

      if (!recipientData.delivery || !recipientData.delivery.date) {
        v.errors = [
          {
            keyword: "checkServiceDateIsSameOrAfterDeliveryDate",
            message: "checkServiceDateIsSameOrAfterDeliveryDate",
            params: [],
            dataPath: `recipients${dataPath}.address.addition2`
          }
        ];
        return false;
      }

      const serviceMoment = moment(recipientData.address.addition2);
      const deliveryMoment = moment(recipientData.delivery.date);

      if (
        !serviceMoment
          .clone()
          .startOf("day")
          .isSameOrAfter(deliveryMoment.startOf("day"))
      ) {
        v.errors = [
          {
            keyword: "checkServiceDateIsSameOrAfterDeliveryDate",
            message: "checkServiceDateIsSameOrAfterDeliveryDate",
            params: [],
            dataPath: `recipients${dataPath}.address.addition2`
          }
        ];
        return false;
      }

      return true;
    }
});

ajv.addKeyword("checkSelectedCardIsValid", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(cardId, dataPath, parentData, parentDataProperty, rootData) {
      const context = getContext();
      if (!context || !cardId) {
        return true;
      }

      const recipientIndex = parseInt(dataPath.match(/\[(\d+)]/, "$1")[1], 10);
      const recipientContext = context.getIn(["recipientContexts", recipientIndex]);
      const recipient = rootData.recipients ? rootData.recipients[recipientIndex] : rootData[recipientIndex];

      if (!recipientContext) {
        return true;
      }

      const availableCards = recipientContext.get("availableCards");
      if (!availableCards || !availableCards.size) {
        return true;
      }

      const selectedCard = availableCards.find(c => c.get("id") === cardId);

      if (!selectedCard) {
        v.errors = [
          {
            keyword: "checkSelectedCardIsValid",
            dataPath: `${dataPath.indexOf(".recipients") === 0 ? "" : ".recipients"}${dataPath}`,
            message: "checkSelectedCardIsValid",
            params: []
          }
        ];
        return false;
      }
      return true;
    }
});

/**
 * Check that the selected item quantity has been spread to all recipients
 *
 * Failure:
 * - if the selected item quantity is more than the spread of all recipients
 *
 * Scope items.item
 */
ajv.addKeyword("checkQuantityIsApplied", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath, parentData, parentDataProperty, rootData) {
      const context = getContext();
      if (!data || !rootData.recipients || !context) {
        return true;
      }

      const allocatedCount = rootData.recipients.reduce((totalAllocated, recipient) => {
        totalAllocated += recipient.products.reduce((quantity, product) => {
          if (product.uuid === data.uuid) {
            quantity += product.quantity;
          }
          return quantity;
        }, 0);
        return totalAllocated;
      }, 0);

      if (allocatedCount === data.quantity) {
        return true;
      }

      v.errors = [
        {
          keyword: "checkQuantityIsApplied",
          dataPath: `${dataPath}.quantity`,
          params: {
            title: getSelectedCartItemTitleFromContext(data, context),
            type: allocatedCount > data.quantity ? "tooMany" : "tooLittle",
            allocated: allocatedCount,
            quantity: data.quantity
          }
        }
      ];

      return false;
    }
});

// TODO items[x].packaging
// TODO ajv.addKeyword('checkPackaging');

ajv.addKeyword("checkPackaging", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      const context = getContext();
      if (!data || !data.webProductId || !data.quantity || !context) {
        return true;
      }

      let packaging = null;

      if (!dataPath) {
        packaging = context.getIn(["currentItemContext", "packaging"]);
      } else {
        const itemContexts = context.get("itemContexts");
        if (!itemContexts || !itemContexts.size) {
          return true;
        }
        const itemContext = itemContexts.find(c => c.get("webProductId") === data.webProductId);
        if (!itemContext) {
          return true;
        }
        packaging = itemContext.get("packaging");
      }
      if (!packaging) {
        return true;
      }

      if (!packaging.find(p => p.get("id") === data.packagingId)) {
        v.errors = [
          {
            keyword: "checkPackaging",
            dataPath: `${dataPath}.packagingId`
          }
        ];
        return false;
      }

      return true;
    }
});

// TODO recipient[x].products
// TODO ajv.addKeyword('checkAtLeastOne', {

ajv.addKeyword("checkRecipientHasAtLeastOneProduct", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      if (!data || !data.products) {
        return true;
      }

      let total = 0;
      data.products.forEach(item => {
        total += item.quantity;
      });

      if (data.products.length === 0 || total === 0) {
        v.errors = [
          {
            keyword: "checkRecipientHasAtLeastOneProduct",
            dataPath: `${dataPath}.products[0].quantity`
          }
        ];
        return false;
      }

      return true;
    }
});

// TODO recipients[x].card.type
// TODO ajv.addKeyword('checkSelectedCardIsValid');

// TODO recipients
// TODO ajv.addKeyword('checkCardTypeShouldBeAllowedByItemProductCollectionMembers');

/**
 * Foreign orders need a phone number!
 */
ajv.addKeyword("checkForeignPhoneRequired", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      if (!data || !data.countryCode || data.countryCode === "NL") {
        return true;
      }

      if (typeof data.phone !== "undefined" && data.phone.length > 0) {
        v.errors = [
          {
            keyword: "required",
            params: {
              missingProperty: "phone"
            },
            dataPath
          }
        ];
        return false;
      }

      return true;
    }
});

/**
 * remove all non digits then check it is at least 10 numbers
 */
ajv.addKeyword("phoneNumber", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      if (!data || !data.replace) {
        return true;
      }

      if (data && (data.countryCode !== "NL" || (data.address && data.address.countryCode !== "NL"))) {
        return true;
      }

      if (data.replace(/\D+/g, "").length < 10) {
        v.errors = [
          {
            keyword: "pattern",
            dataPath
          }
        ];
        return false;
      }

      return true;
    }
});

// TODO checkout.payment.type
// TODO ajv.addKeyword('checkSelectedPaymentMethodIsValid');
// TODO ajv.addKeyword('checkPaymentType');

ajv.addKeyword("checkBillinkRequiredCoc", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data) {
      const isBillink = parseInt(getIn(data, "checkout.payment.type"), 10) === 20964521;
      if (!isBillink || getIn(data, "customer.type") === "person" || getIn(data, "customer.cocNumber")) {
        return true;
      }

      v.errors = [
        {
          keyword: "checkBillinkRequiredCoc",
          // message: i18n.t('checkSelectedCardIsValid'),
          params: {},
          dataPath: ".customer.cocNumber"
        }
      ];
      return false;
    }
});

ajv.addKeyword("checkPaymentType2", {
  // compile: (schema, parentSchema) => function v(data, dataPath, parentData, parentDataProperty, rootData) {
  compile: () =>
    function v(data, dataPath) {
      const context = getContext();
      if (!context) {
        return true;
      }

      v.errors = [
        {
          keyword: "required",
          params: {
            missingProperty: "type"
          },
          dataPath
        }
      ];

      if (!data.payment.type) {
        v.errors = [
          {
            keyword: "required",
            params: {
              missingProperty: "type"
            },
            dataPath
          }
        ];
        return false;
      }

      v.errors = [
        {
          keyword: "required",
          params: {
            missingProperty: "type"
          },
          dataPath
        }
      ];
      return false;
    }
});

const createSchemaValidator = schema => {
  if (!schema) {
    return ajv;
  }

  if (schema.get("$id") === "Product") {
    ajv.removeSchema("Product");
    ajv.compile(
      schema
        .delete("$schema")
        .set("$id", "Product")
        .toJS()
    );
    return ajv;
  }

  // console.log(schema.toJS());

  ajv.removeSchema("Cart");
  ajv.compile(
    schema
      .delete("$schema")
      .set("$id", "Cart")
      .toJS()
  );

  ajv.removeSchema("Step1");
  ajv.compile(
    schema
      .getIn(["properties", "items"])
      .set("$id", "Step1")
      .toJS()
  );

  ajv.removeSchema("Step2");
  ajv.compile(
    schema
      .getIn(["properties", "recipients"])
      .set("$id", "Step2")
      .toJS()
  );

  ajv.removeSchema("Step3");
  ajv.compile(
    schema
      .getIn(["properties", "customer"])
      .set("$id", "Step3")
      .toJS()
  );

  ajv.removeSchema("Step4");
  ajv.compile(
    schema
      .getIn(["properties", "checkout"])
      .set("$id", "Step4")
      .toJS()
  );

  return ajv;
};

export default createSchemaValidator;
